import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import type { Component } from "vue";
import { defineAsyncComponent } from "vue";

export type ClientSectionGroupId
  = | "general"
    | "organisation"
    | "operations"
    | "finance"
    | "automation"
    | "communication"
    | "management";

/**
 * `page` sections scroll as a whole document; `fill` sections are overviews that size a table to the
 * available height and scroll inside it.
 */
export type ClientSectionLayout = "page" | "fill";

export interface ClientSection {
  id: string
  group: ClientSectionGroupId | null
  icon: IconName
  titleKey: string
  layout: ClientSectionLayout
  component: Component
  props?: (clientIri: string) => Record<string, unknown>
  requiresNotificationsV2?: boolean
  /**
   * Stays mounted (hidden) after the first visit. A form needs this: remounting its fields would make
   * vee-validate forget which of them are dirty, so leaving and coming back would lose unsaved edits.
   */
  keepMounted?: boolean
}

export interface ClientSectionContext {
  notificationsV2: boolean
}

export const CLIENT_SECTION_GROUPS: { id: ClientSectionGroupId, titleKey: string, icon: IconName }[] = [
  { id: "general", titleKey: "clients.detail.groups.general", icon: "building" },
  { id: "organisation", titleKey: "clients.detail.groups.organisation", icon: "network" },
  { id: "operations", titleKey: "clients.detail.groups.operations", icon: "house" },
  { id: "finance", titleKey: "clients.detail.groups.finance", icon: "coins" },
  { id: "automation", titleKey: "clients.detail.groups.automation", icon: "zap" },
  { id: "communication", titleKey: "clients.detail.groups.communication", icon: "bell" },
  { id: "management", titleKey: "clients.detail.groups.management", icon: "tool-case" },
];

function clientOverride(clientIri: string) {
  return { clientOverride: clientIri };
}

export const CLIENT_SECTIONS: ClientSection[] = [
  {
    id: "overview",
    group: null,
    icon: "layout-grid",
    titleKey: "clients.detail.sections.overview.title",
    layout: "page",
    component: defineAsyncComponent(() => import("./sections/ClientOverviewSection.vue")),
  },
  {
    id: "details",
    group: "general",
    icon: "id-card",
    titleKey: "clients.detail.sections.details.title",
    layout: "page",
    component: defineAsyncComponent(() => import("./sections/ClientDetailsSection.vue")),
    keepMounted: true,
  },
  {
    id: "multilingual",
    group: "general",
    icon: "globe",
    titleKey: "settings.sidebar.multilingual",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/Multilingual/MultilingualSettingsPage.vue")),
    props: clientOverride,
  },
  {
    id: "locations",
    group: "organisation",
    icon: "map-pin",
    titleKey: "settings.sidebar.locations",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/components/Features/Locations/LocationsOverview.vue")),
    props: clientOverride,
  },
  {
    id: "users",
    group: "organisation",
    icon: "users",
    titleKey: "settings.sidebar.users",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/Users/UsersOverview.vue")),
    props: clientOverride,
  },
  {
    id: "roles",
    group: "organisation",
    icon: "lock",
    titleKey: "settings.sidebar.roles",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/PermissionTemplateGroups/PermissionTemplateGroupsOverview.vue")),
    props: clientOverride,
  },
  {
    id: "objects",
    group: "operations",
    icon: "house",
    titleKey: "settings.sidebar.objects",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/Properties/PropertyOverview.vue")),
    props: clientOverride,
  },
  {
    id: "object-groups",
    group: "operations",
    icon: "layout-dashboard",
    titleKey: "settings.sidebar.groups",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/PropertyGroups/PropertyGroupOverview.vue")),
    props: clientOverride,
  },
  {
    id: "sorts",
    group: "operations",
    icon: "shuffle",
    titleKey: "settings.sidebar.sorts",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/Sorts/SortsOverview.vue")),
    props: clientOverride,
  },
  {
    id: "report-topics",
    group: "operations",
    icon: "list-check",
    titleKey: "settings.sidebar.report_topics",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/ReportTopics/ReportTopicOverview.vue")),
    props: clientOverride,
  },
  {
    id: "departments",
    group: "operations",
    icon: "presentation",
    titleKey: "settings.sidebar.departments",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/UserGroups/UserGroupsOverview.vue")),
    props: clientOverride,
  },
  {
    id: "reservations",
    group: "operations",
    icon: "calendar-days",
    titleKey: "settings.sidebar.reservations",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/Reservations/ReservationOverview.vue")),
    props: clientOverride,
  },
  {
    id: "products",
    group: "finance",
    icon: "package-open",
    titleKey: "settings.sidebar.products",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/Products/ProductOverview.vue")),
    props: clientIri => ({ clientOverride: clientIri, viewGlobal: true }),
  },
  {
    id: "general-ledgers",
    group: "finance",
    icon: "book",
    titleKey: "settings.sidebar.general_ledgers",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/GeneralLedgers/GeneralLedgerOverview.vue")),
    props: clientOverride,
  },
  {
    id: "integrations",
    group: "automation",
    icon: "store",
    titleKey: "settings.sidebar.links",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/AppStore/AppStoreOverview.vue")),
    props: clientOverride,
  },
  {
    id: "event-automations",
    group: "automation",
    icon: "zap",
    titleKey: "clients.detail.sections.event_automations.title",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/Automation/views/AutomationTopicOverview.vue")),
    props: clientOverride,
  },
  {
    id: "recurring-automations",
    group: "automation",
    icon: "refresh-cw",
    titleKey: "clients.detail.sections.recurring_automations.title",
    layout: "fill",
    component: defineAsyncComponent(() => import("@verbleif/shared/src/composables/Automation/views/AutomationRecurringOverview.vue")),
    props: clientOverride,
  },
  {
    id: "notifications",
    group: "communication",
    icon: "bell",
    titleKey: "settings.sidebar.notifications",
    layout: "page",
    component: defineAsyncComponent(() => import("./sections/ClientNotificationsSection.vue")),
    requiresNotificationsV2: true,
  },
  {
    id: "preferences",
    group: "communication",
    icon: "sliders-horizontal",
    titleKey: "users.preferences.tab_title",
    layout: "page",
    component: defineAsyncComponent(() => import("./sections/ClientPreferencesSection.vue")),
  },
  {
    id: "usage",
    group: "management",
    icon: "gauge",
    titleKey: "usage.title",
    layout: "page",
    component: defineAsyncComponent(() => import("./sections/ClientUsageSection.vue")),
  },
  {
    id: "demo-data",
    group: "management",
    icon: "wand-sparkles",
    titleKey: "demo_importer.section_label",
    layout: "fill",
    component: defineAsyncComponent(() => import("./sections/ClientDemoDataSection.vue")),
  },
  {
    id: "advanced",
    group: "management",
    icon: "toolbox",
    titleKey: "clients.detail.sections.advanced.title",
    layout: "page",
    component: defineAsyncComponent(() => import("./sections/ClientAdvancedSection.vue")),
  },
];

export const DEFAULT_CLIENT_SECTION = "overview";

/** The tab order of the old client dialog, so `?clientTab=<n>` links still land on the right section. */
const LEGACY_CLIENT_TABS = [
  "details",
  "locations",
  "integrations",
  "event-automations",
  "recurring-automations",
  "usage",
  "notifications",
  "preferences",
  "multilingual",
  "multilingual",
];

/** Sections that were merged into another one; their old addresses open the section they are part of now. */
const SECTION_ALIASES: Record<string, string> = {
  "languages": "multilingual",
  "translation-context": "multilingual",
};

export function legacyClientTabToSection(tab: unknown): string | null {
  const raw = Array.isArray(tab) ? tab[0] : tab;
  if (typeof raw !== "string" || !/^\d+$/.test(raw)) {
    return null;
  }
  return LEGACY_CLIENT_TABS[Number(raw)] ?? null;
}

export function visibleClientSections(context: ClientSectionContext): ClientSection[] {
  return CLIENT_SECTIONS.filter(section => !section.requiresNotificationsV2 || context.notificationsV2);
}

export function findClientSection(id: unknown, context: ClientSectionContext): ClientSection | null {
  if (typeof id !== "string") {
    return null;
  }
  const resolved = SECTION_ALIASES[id] ?? id;
  return visibleClientSections(context).find(section => section.id === resolved) ?? null;
}
